import React from "react";
import { Mail, Shield, Clock } from "lucide-react";
import LegalShell, { Section } from "../components/LegalShell";

const EFFECTIVE = "7 June 2026";

export default function DataDeletion() {
  const subject = encodeURIComponent("TrainMate: Data deletion request");
  const body = encodeURIComponent(
    "Hi TrainMate team,\n\n" +
    "I would like to request deletion of my TrainMate account and all associated personal data.\n\n" +
    "Account email: <the email you sign in with>\n" +
    "Reason (optional): \n\n" +
    "I understand that GST invoice records will be retained for the 5-year period required by the ATO, and that anonymised aggregate metrics may be kept for platform statistics.\n\n" +
    "Thanks,\n"
  );

  return (
    <LegalShell title="Data Deletion" kicker="Your right to be forgotten" effective={EFFECTIVE}>
      <p>
        You can permanently delete your TrainMate account and all data associated with it at any time. This
        page tells you exactly how, what gets deleted, what has to be kept, and when.
      </p>

      {/* Highlight card with the action */}
      <div className="tm-card p-6 sm:p-8 border-l-4 border-[#0F6B6B]" data-testid="data-deletion-action">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-[#0F6B6B]/15 border border-[#0F6B6B]/40 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-[#0F6B6B]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#0F6B6B] font-semibold mb-2">How to request</p>
            <h2 className="font-display text-2xl font-black uppercase mb-3">Email us and we handle the rest</h2>
            <p className="text-[#2A2623] text-sm mb-5">
              Send a deletion request from the Google account you sign in with, so we can verify your
              identity. We'll process it within <strong>30 days</strong> and confirm by email when complete.
            </p>
            <a
              href={`mailto:privacy@trainmate.au?subject=${subject}&body=${body}`}
              className="inline-flex items-center justify-center text-center gap-2 min-h-[44px] py-2.5 px-4 sm:px-5 rounded-md bg-[#0F6B6B] text-[#FFFFFF] font-bold text-xs sm:text-sm tracking-normal sm:tracking-wide uppercase hover:bg-[#0B5757] transition-colors max-w-full break-words"
              data-testid="data-deletion-email-button"
            >
              <Mail className="w-4 h-4 shrink-0" />
              Email privacy@trainmate.au
            </a>
          </div>
        </div>
      </div>

      <Section heading="What gets deleted">
        <ul className="list-disc pl-6 space-y-1 text-[#2A2623]">
          <li>Your trainer profile (name, business name, ABN, phone, default rate, timezone, currency)</li>
          <li>All client records you added (names, contact details, goals, notes, session history)</li>
          <li>Sessions, session packs, and associated metadata</li>
          <li>Session cookies and login history</li>
          <li>Your Google identifier and profile picture URL</li>
        </ul>
      </Section>

      <Section heading="What we have to keep (and why)">
        <div className="flex items-start gap-3 tm-card p-4 border-l-2 border-[#D9A441]">
          <Shield className="w-5 h-5 text-[#7A5E18] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#2A2623] mb-1">GST invoice records. Retained 5 years</p>
            <p className="text-sm text-[#2A2623]">
              Australian tax law (ATO) requires us to retain GST-compliant invoices you issued for
              <strong> five years </strong>from their issue date. These are kept in a locked-down archive,
              accessible only for legal or tax-compliance purposes, and are not used for any other reason.
            </p>
          </div>
        </div>
        <p className="text-sm text-[#6B635C]">
          We may also retain anonymised aggregate metrics (e.g. "number of accounts created per week")
          that cannot be linked back to you.
        </p>
      </Section>

      <Section heading="How long it takes">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-[#6B635C] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#2A2623]">
            <p><strong className="text-[#2A2623]">Acknowledgement:</strong> within 2 business days.</p>
            <p><strong className="text-[#2A2623]">Full deletion:</strong> within 30 days of your verified request.</p>
            <p><strong className="text-[#2A2623]">Backup rotation:</strong> deleted data will also disappear from all encrypted backups within a further 12 months as the backup retention window rolls forward.</p>
          </div>
        </div>
      </Section>

      <Section heading="Deleting a single client (not your whole account)">
        <p>
          If you only want to delete one client (for example, they've asked you to remove their record),
          sign in and open the <strong>Clients</strong> tab → open the client → tap "Delete client". The
          record and its sessions are removed immediately. Any invoices issued to that client are anonymised
          on your side but the 5-year ATO retention still applies to the invoice document itself.
        </p>
      </Section>

      <Section heading="Not what you were looking for?">
        <p>
          If you were trying to <strong>export</strong> your data rather than delete it, email the same
          address and ask for an export instead. We'll send you a JSON file within 30 days at no charge.
        </p>
      </Section>
    </LegalShell>
  );
}
